import type { TPlayer } from 'video.js'

import videojs from 'video.js'
import { bind_ } from '|>/shared/fn'
import { on, register } from '|>/shared/vjs'
import { BaseVolumePanel } from '../base'
import { MuteToggle } from './mute-toggle'
import { VolumeControl } from './volume-control'

import './volume-bar.css'

// time to show full volume bar before hidden state
const FULL_VIEW_TIMEOUT = 3000

@register
export class VolumePanel extends BaseVolumePanel {
  declare muteToggle: MuteToggle
  declare volumeControl: VolumeControl

  fullViewTimeoutId?: number

  isMouseOverToggle: boolean
  isMouseOverSlider: boolean

  constructor(player: TPlayer, options: any) {
    super(player, options)

    this.isMouseOverToggle = false
    this.isMouseOverSlider = false

    this.checkMouseOverToggle = bind_(this, this.checkMouseOverToggle)
    this.checkMouseOverSlider = bind_(this, this.checkMouseOverSlider)
    this.checkMouseOutToggle = bind_(this, this.checkMouseOutToggle)
    this.checkMouseOutSlider = bind_(this, this.checkMouseOutSlider)
    this.toggleSliderVisibility = bind_(this, this.toggleSliderVisibility)
    this.handleArrows = bind_(this, this.handleArrows)

    this.addKeyboardHandlers()
  }

  get isMouseOver() {
    return this.isMouseOverToggle || this.isMouseOverSlider
  }

  override handleMouseOver(event: MouseEvent) {
    this.player_.addClass('vjs-x-mute-control')
    super.handleMouseOver(event)
  }

  override handleMouseOut(event: MouseEvent) {
    this.player_.removeClass('vjs-x-mute-control')
    super.handleMouseOut(event)
  }

  addKeyboardHandlers() {
    videojs.on(this.muteToggle.el_, 'mouseover', this.checkMouseOverToggle)
    videojs.on(this.volumeControl.el_, 'mouseover', this.checkMouseOverSlider)
    videojs.on(this.muteToggle.el_, 'mouseout', this.checkMouseOutToggle)
    videojs.on(this.volumeControl.el_, 'mouseout', this.checkMouseOutSlider)
  }

  removeKeyboardHandlers() {
    videojs.off(this.muteToggle.el_, 'mouseover', this.checkMouseOverToggle)
    videojs.off(this.volumeControl.el_, 'mouseover', this.checkMouseOverSlider)
    videojs.off(this.muteToggle.el_, 'mouseout', this.checkMouseOutToggle)
    videojs.off(this.volumeControl.el_, 'mouseout', this.checkMouseOutSlider)
  }

  checkMouseOverToggle() {
    this.isMouseOverToggle = true
  }
  checkMouseOutToggle() {
    this.isMouseOverToggle = false
  }
  checkMouseOverSlider() {
    this.isMouseOverToggle = true
  }
  checkMouseOutSlider() {
    this.isMouseOverToggle = false
  }

  handleArrows(event: KeyboardEvent) {
    if (
      this.player_.hasClass('vjs-controls-disabled') ||
      this.player_.hasClass('vjs-error')
    ) {
      return
    }

    switch (event.key) {
      case 'ArrowUp': {
        this.player_.reportUserActivity()
        this.player_.userActive(true)
        if (this.player_.volume()) this.player_.muted(false)
        this.player_.volume(Math.min(this.player_.volume() + 0.05, 1))
        break
      }
      case 'ArrowDown': {
        this.player_.reportUserActivity()
        this.player_.userActive(true)
        this.player_.volume(Math.max(this.player_.volume() - 0.05, 0))
        break
      }
    }
  }

  @on('volumechange')
  toggleSliderVisibility() {
    this.volumeControl.makeActive()
    this.clearTimeout(this.fullViewTimeoutId!)
    this.fullViewTimeoutId = this.setTimeout(() => {
      if (this.isMouseOver) return
      this.volumeControl.makeInactive()
    }, FULL_VIEW_TIMEOUT)
  }

  override dispose() {
    this.removeKeyboardHandlers()
    super.dispose()
  }
}

VolumePanel.options = {
  className: 'vjs-x-volume-panel-horizontal',
  children: [MuteToggle.as('muteToggle'), VolumeControl.as('volumeControl')],
}
